import {
  HistInfo,
  RechtMasterListEntry,
  RechtVersionListEntry,
  StandardrechtMasterDetailEntryMeta,
  StandardrechtVersionDetailEntryMeta
} from '../../../core/gb-api';
import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { NgTemplateOutlet, PlatformLocation } from '@angular/common';
import { KompaktdarstellungTemplateComponent } from '../kompakt-darstellung-template.component';
import { ComponentLocationStrategy } from '../../services/location-strategy/component-location-strategy';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { LinkComponent, NavTarget } from '../../components/link/link.component';
import { TranslateNamePipe } from '../../pipes/simple/translate-name.pipe';
import { BetragPipe } from '../../pipes/simple/betrag.pipe';
import { DatumPipe } from '../../pipes/simple/datum.pipe';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ReadMoreComponent } from '../../widgets/read-more/read-more.component';
import { HistoryDirective } from '../../directives/history.directive';
import { FormatRechteIdPipe } from '../../pipes/format/format-rechte-id.pipe';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'cap-kompakt-standardrecht',
  templateUrl: './kompakt-standardrecht.component.html',
  preserveWhitespaces: false,
  imports: [NgTemplateOutlet, LinkComponent, ReadMoreComponent, HistoryDirective, TranslatePipe, FormatRechteIdPipe]
})
export class KompaktStandardrechtComponent extends KompaktdarstellungTemplateComponent implements OnChanges, OnInit {
  navTargets = NavTarget;

  // Wenn RechtMasterListEntry muss die version nicht mitgegeben werden
  @Input() recht: StandardrechtMasterDetailEntryMeta | RechtMasterListEntry;
  @Input() version: StandardrechtVersionDetailEntryMeta | RechtVersionListEntry;
  @Input() histInfo: HistInfo;
  @Input() textLimit = 9999;

  @ViewChild('typ00', { static: true }) typ00Template: TemplateRef<any>;
  @ViewChild('typ01', { static: true }) typ01Template: TemplateRef<any>;

  text = '';
  rechtArt = '';
  private isStichwortActive: boolean = false;
  enableNavigation: boolean = false;

  constructor(
    formatService: FormatConfigService,
    location: PlatformLocation,
    locationStrategy: ComponentLocationStrategy,
    protected appformatConfigService: FormatConfigService,
    private translateName: TranslateNamePipe,
    private betragPipe: BetragPipe,
    private datum: DatumPipe,
    private translate: TranslateService
  ) {
    super(formatService, 'STANDARDRECHT', location, locationStrategy);
    keysToTranslate<string>([
      'DIENSTBARKEIT_KURZ',
      'ANMERKUNG_KURZ',
      'VORMERKUNG_KURZ',
      'GRUNDLAST_KURZ',
      'PFANDRECHT_KURZ',
      'SDR_DIENSTBARKEIT_KURZ'
    ]);
    translate.onLangChange.subscribe(() => this.ngOnChanges(null));
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.addSub(
      this.appformatConfigService.parameterConfig$.subscribe((config) => {
        this.isStichwortActive = !!config.parameters.STICHWORT_ANZEIGEN_DRUCKEN;
        this.loadText();
      })
    );
    this.enableNavigation = this.recht.isNavigierbar && environment.deploymentMode !== 'PUBLIC';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.histInfo && 'histInfo' in this.recht) {
      this.histInfo = this.recht.histInfo;
    }
    this.loadText();
  }

  loadText(): void {
    this.text = '';
    if (this.recht && 'art' in this.recht && 'version' in this.recht) {
      this.rechtArt = this.recht.art;
      this.version = this.recht.version;
    } else if (this.recht && 'rechtArt' in this.recht) {
      this.rechtArt = this.recht.rechtArt;
    }
    if (this.version) {
      if (this.version.stichwort && this.version.stichwortZusatz && this.isStichwortActive) {
        this.text += this.translateName.transform(this.version.stichwort.text);
      }
      if (this.version.stichwort && !this.version.stichwortZusatz) {
        this.text += this.translateName.transform(this.version.stichwort.text);
      }
      if (this.version.stichwortZusatz) {
        this.text += ' ' + this.version.stichwortZusatz;
      }
      if (
        'gesamtwert' in this.version &&
        this.version.gesamtwert > 0 &&
        !this.formatMatches(['STANDARDRECHT_TYP02', 'STANDARDRECHT_TYP03'])
      ) {
        this.text += ', CHF ' + this.betragPipe.transform(this.version.gesamtwert);
      }
      if (this.version.ablaufdatum && !this.formatMatches(['STANDARDRECHT_TYP02', 'STANDARDRECHT_TYP03'])) {
        this.text += `, ${this.translate.instant('bis')} ${this.datum.transform(this.version.ablaufdatum)}`;
      }
    }
  }

  registerTemplates(): void {
    this.bindTemplates(this.typ00Template, 'STANDARDRECHT_TYP00');
    this.bindTemplates(this.typ01Template, 'STANDARDRECHT_TYP01');
    this.bindTemplates(this.typ01Template, 'STANDARDRECHT_TYP02');
    this.bindTemplates(this.typ01Template, 'STANDARDRECHT_TYP03');
  }

  getQueryParams(version: StandardrechtMasterDetailEntryMeta | RechtMasterListEntry) {
    return { id: this.recht.id.id, versionId: version.id.id };
  }

  getAbkuerzungKey(input: string): string {
    return input + '_KURZ';
  }

  getDefaultTemplate(): TemplateRef<any> {
    return this.typ00Template;
  }

  protected readonly environment = environment;
}
